import ActivityManager from "@/activity/ActivityManager";
import RoleIsAssignedException from "@/server/com/silkplm/rpc/exceptions/RoleIsAssignedException";
import RoleIsNotAvailableException from "@/server/com/silkplm/rpc/exceptions/RoleIsNotAvailableException";
import NoOwnerException from "@/server/com/silkplm/rpc/exceptions/NoOwnerException";
import LastOwnerCannotBeDeletedException from "@/server/com/silkplm/rpc/exceptions/LastOwnerCannotBeDeletedException";
import EventBus from "@/utils/EventBus";
import Events from "@/utils/Events";

const UserEditPanel = () => require("@/components/forms/customizations/plm/UserEditPanel");
const EditUsers = () => require("@/components/forms/customizations/plm/EditUsers");
const OrganizationSetup = () => require("@/components/organization/OrganizationSetupActivity");
const UserNotificationsPanel = () => require("@/components/notifications/UserNotificationsPanel");
const DashboardUsers = () => require("@/components/forms/customizations/plm/DashboardUsers");


/**
 @@action {
    name: "Show users"
    patterns: "Show users", "Users", "List users"
     icon: "user"
  }

 @@action {
    name: "Remove user"
    patterns: "Remove user", "Delete user", "Remove user"
    icon: "user"
  }

 @@action{
    name: "Change user password"
    patterns: "Reset user password", "Change user password"
    icon: "user"
  }

 @returns {Promise<void>}
 */

export async function showUsers(){
    await ActivityManager.showActivity(DashboardUsers);
}

/**
  @@action {
    name: "Add user"
    patterns: "Add user", "Create user", "New login", "Add sub-account",
    icon: "user-plus"
  }

  @returns {Promise<void>}
*/
export async function addUser(){
    await ActivityManager.replaceActivity(EditUsers, {mode: "NEW"});
}

/**

 @@action{
    name: "Remove $ApplicationUser"
    patterns: "Remove $ApplicationUser", "delete $ApplicationUser"
    icon: "user-minus"
  }

 @param applicationUser {ApplicationUser}
 @returns {Promise<void>}
 */

export async function deleteUser(applicationUser){
    await ActivityManager.replaceActivity(DashboardUsers, {mode: "DELETE", userId:applicationUser.id});
}

/**
 @@action{
    name: "edit $ApplicationUser"
    patterns: "edit $ApplicationUser", "$ApplicationUser", "open $ApplicationUser", "Change $ApplicationUser", "Change $ApplicationUser password"
    icon: "user-pen"
  }

 @@action{
    name: "Change $ApplicationUser password"
    patterns: "Change $ApplicationUser password", "Reset $ApplicationUser password"
    icon: "user"
  }


 @param applicationUser {ApplicationUser}
 @returns {Promise<void>}
 */
export async function editSelectedUser(applicationUser){
    await ActivityManager.replaceActivity(EditUsers, {mode: "EDIT_AS_ADMIN", userId:applicationUser.id});
}


/**
 @@action{
    name: "Change my password"
    patterns: "Change my password", "Reset password", "Reset my password"
    icon: "user"
  }
 */
export async function changeMyPassword(){
        await ActivityManager.showActivity(UserEditPanel);
}

/**
 @@action {
    name: "Change organization logo"
    patterns: "Change organization logo", "Login page logo", "Navigation bar logo"
    icon: "pencil-alt"
  }

 @@action {
    name: "Change organization name"
    patterns: "Change organization name", "Add organization name", "Company short name", "Company full name"
    icon: "pencil-alt"
  }

 @@permissions: "ORGANIZATION_SETTINGS"

 @returns {Promise<void>}
 */
export async function changeOrganization(){
    await ActivityManager.showActivity(OrganizationSetup);
}

/**
 @@action {
    name: "Change organization domain"
    patterns: "Change organization domain"
    icon: "pencil-alt"
  }

 @@permissions: "ORGANIZATION_SETTINGS"

 @returns {Promise<void>}
 */
export async function changeOrganizationDomain(){
    await ActivityManager.showActivity(OrganizationSetup, {activeBranding: false});
}

/**
 @@action {
    name: "Show notifications"
    patterns: "Show notifications"
    icon: "envelope"
  }

 @returns {Promise<void>}
 */
export async function showNotifications(){
    await ActivityManager.showActivity(UserNotificationsPanel);
}


/**
 @@action{
    name: "Change $ApplicationUser roles"
    patterns: "Change $ApplicationUser roles", "Set $ApplicationUser roles", "Edit $ApplicationUser roles"
    icon: "user"
  }


 @param applicationUser {ApplicationUser}
 @returns {Promise<void>}
 */
export async function changeUserRoles(applicationUser){
    await ActivityManager.replaceActivity(EditUsers, {mode: "EDIT_AS_ADMIN", userId:applicationUser.id, activeBasicData: false});
}

/**
 @@action{
    name: "Add role to user"
    patterns: "Add $Role role to $ApplicationUser"
    icon: "user"
  }

 */

export async function addRoleToUser(role, applicationUser){
    if(await DialogManager.yesNo($tr("addRole","Add role"),
        $tr("actions.sure.add.role","Are you sure you want to add this role?"))){
        try {
            await Rpc.Users.addRoleToUser(applicationUser, role);
            EventBus.$emit(Events.roleChanged);
        } catch (e) {
            if (e instanceof RoleIsAssignedException)
                await DialogManager.info($tr("actions.role.assigned","The role is already assigned"),
                    $tr("actions.operation.cannot.performed.","The operation cannot be performed."));
            else if(e instanceof RoleIsNotAvailableException){
                await DialogManager.info($tr("actions.role.not.available.for.selected","The role is not available for the selected user"),
                    $tr("actions.operation.cannot.performed","The operation cannot be performed"));
            }
            else if(e instanceof NoOwnerException)
                await DialogManager.info($tr("actions.role.requires.user.email",
                    "Assigning the owner role requires that the user has an email."),
                    $tr("actions.operation.cannot.performed","The operation cannot be performed"));
        }
    }
}

/**
 @@action{
    name: "Delete role from user"
    patterns: "Delete $Role from $ApplicationUser"
    icon: "user"
  }

 */

export async function deleteRoleFromUser(role, applicationUser){
    if(await DialogManager.yesNo($tr("deletingRole","Deleting role"),
        $tr("actions.sure.delete.role","Are you sure you want to delete this role?"))){
        try {
            await Rpc.Users.deleteRoleFromUser(applicationUser, role);
            EventBus.$emit(Events.roleChanged);
        } catch (e) {
            if (e instanceof LastOwnerCannotBeDeletedException)
                await DialogManager.info($tr("actions.cannot.operation.must.have.owner",
                        "The operation cannot be performed because the organization must have at least one owner."),
                    "The operation cannot be performed.");
            else if (e instanceof RoleIsNotAvailableException)
                await DialogManager.info($tr("actions.not.available.selected.user","The role is not available for the selected user."),
                    $tr("actions.operation.cannot.be.performed",
                        "The operation cannot be performed."));
        }
    }
}

/**
 @@action{
    name: "Add attachment to my profile"
    patterns: "Add attachment to my profile", "Create attachment in my profile password", "Add new attachment to my profile"
    icon: "paperclip"
  }

 @@action {
    name: "Show my attachments"
    patterns: "Show my attachments", "List of my attachments", "Show attachments from my profile"
    icon: "paperclip"
  }

 @@action {
    name: "Delete attachment from my profile"
    patterns: "Delete attachment from my profile", "Remove attachment from my profile"
    icon: "paperclip"
  }

 @returns {Promise<void>}
 */

export async function addAttachmentToMyProfile(){
    /** @type {UserEditPanel} */
    let res = await ActivityManager.showActivity(UserEditPanel);
    await res.openAttachments();
}

/**
 @@action{
    name: "Show $ApplicationUser attachments"
    patterns: "Show $ApplicationUser attachments", "List of attachments from user $ApplicationUser, "Show attachments from $ApplicationUser"
    icon: "paperclip"
  }

 @@action{
    name: "Add attachment to $ApplicationUser"
    patterns: "Add attachment to $ApplicationUser", "Create attachment in $ApplicationUser", "Add new attachment to $ApplicationUser"
    icon: "paperclip"
  }

 @@action {
    name: "Delete attachment from $ApplicationUser"
    patterns: "Delete attachment from $ApplicationUser", "Remove attachment from user $ApplicationUser"
    icon: "paperclip"
  }


 @@permissions: "USER_EDIT"
 @param applicationUser {ApplicationUser}
 @returns {Promise<void>}
 */

export async function addAttachmentToUser(applicationUser){
    /** @type {EditUsers} */
    let res = await ActivityManager.replaceActivity(EditUsers, {mode: "EDIT_AS_ADMIN", readOnlyInputs: false, userId: applicationUser.id});
    await res.openAttachments();
}